import React, { useContext, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton } from "src/components/GlassButtons";
import { ANNManager } from "./ANNManager";
import { AnnotationType, AnnotationContextType, type ANNAnnotationRecord, ANNOTATION_TYPES, type AnnotationTypeCategory, AnnotationQuality } from "./ANNTypes";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { ANNActionType, ANNContext } from "./ANNState";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
interface BaseCreateModalProps {
   open: boolean;
   contextType: AnnotationContextType;
   documentId?: string;
   bfeNumber?: string | number;
   datoloebenr?: string;
   onClose: () => void;
   onCreated?: (annotation: ANNAnnotationRecord) => void;
   editAnnotation?: ANNAnnotationRecord;
   oldAnnotationId?: string;
}
export interface ANNCreateNoteProps extends BaseCreateModalProps { }
export function ANNCreateNote(props: ANNCreateNoteProps) {
   const { open, contextType, documentId, bfeNumber, datoloebenr, onClose, onCreated, editAnnotation } = props;
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: annDispatch } = useContext(ANNContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const canVerify = hasAccessToFeature(Feature.AnnotationAuthUser);
   const organizationId = sessionState?.customerRef;
   const [title, setTitle] = useState(editAnnotation?.title || "");
   const [text, setText] = useState(editAnnotation?.note?.text || "");
   const [annotationType, setAnnotationType] = useState<AnnotationTypeCategory>(editAnnotation?.annotationType || "Notat");
   const [quality, setQuality] = useState<AnnotationQuality | undefined>(editAnnotation?.quality);
   const [verifyStamp, setVerifyStamp] = useState(false);
   const [historyComment, setHistoryComment] = useState("");
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState<string | null>(null);

   useEffect(() => {
      if (editAnnotation) {
         setTitle(editAnnotation.title || "");
         setText(editAnnotation.note?.text || "");
         setAnnotationType(editAnnotation.annotationType || "Notat");
         setQuality(editAnnotation.quality);
      } else {
         setTitle("");
         setText("");
         setAnnotationType("Notat");
         setQuality(undefined);
      }
      setHistoryComment("");
   }, [editAnnotation]);

   if (!open) return null;
   async function handleCreate() {
      if (contextType === AnnotationContextType.Servitut && !documentId) {
         setError(Localization.getText("No document selected"));
         return;
      }
      if (contextType === AnnotationContextType.Ejendom && !bfeNumber) {
         setError(Localization.getText("No BFE number available"));
         return;
      }
      if (!organizationId) {
         setError(Localization.getText("No organization selected"));
         return;
      }
      if (!title.trim()) {
         setError(Localization.getText("Title is required"));
         return;
      }
      if (!text.trim()) {
         setError(Localization.getText("Note text is required"));
         return;
      }
      setSaving(true);
      setError(null);
      annDispatch({ type: ANNActionType.SaveAnnotationRequest });
      try {
         const record = await ANNManager.saveAnnotation({
            contextType,
            documentId,
            bfeNumber,
            datoloebenr,
            organizationId,
            type: AnnotationType.Note,
            annotationType,
            quality,
            verified: canVerify && verifyStamp,
            title: title.trim(),
            historyComment: historyComment.trim() || undefined,
            note: {
               comment: title.trim(),
               text: text.trim(),
            },
            userId: sessionState?.userRef || "anonymous",
            userName: sessionState?.userName || sessionState?.userRef || "anonymous",
            id: editAnnotation?.id,
            originalCreatedAt: editAnnotation?.createdAt,
            originalCreatedBy: editAnnotation?.createdBy,
            originalCreatedByName: editAnnotation?.createdByName,
         });
         annDispatch({ type: ANNActionType.SaveAnnotationSuccess, payload: record });
         onCreated?.(record);
         setTitle("");
         setText("");
         setQuality(undefined);
         setHistoryComment("");
         onClose();
      } catch (err: any) {
         annDispatch({ type: ANNActionType.SaveAnnotationFailure, payload: err?.message || String(err) });
         setError(err?.message || String(err));
      } finally {
         setSaving(false);
      }
   }
   return ReactDOM.createPortal(
      <AdvancedDragModal
         title={editAnnotation ? Localization.getText("Edit note") : Localization.getText("Create note")}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ left: "25%", top: "15%", width: "520px" }}
         onClose={onClose}
      >
         <ADModalBody>
            <div className="ann-create-form-container">
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Annotation Type")}
                  </label>
                  <select
                     value={annotationType}
                     onChange={e => setAnnotationType(e.target.value as AnnotationTypeCategory)}
                     className="ann-create-select"
                  >
                     {ANNOTATION_TYPES.map(type => (
                        <option key={type} value={type}>{Localization.getText(type)}</option>
                     ))}
                  </select>
               </div>
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Quality Assessment")}
                  </label>
                  <div className="ann-create-quality-selector-group">
                     <div
                        onClick={() => setQuality(undefined)}
                        className={`ann-create-quality-selector ${quality === undefined ? "ann-create-quality-selector-none ann-create-quality-selector-selected" : ""}`}
                     >
                        <span className="ann-create-quality-label">{Localization.getText("None")}</span>
                     </div>
                     {[AnnotationQuality.Gold, AnnotationQuality.Silver, AnnotationQuality.Bronze].map((q) => {
                        const isSelected = quality === q;
                        const label = Localization.getText(q);
                        return (
                           <div
                              key={q}
                              onClick={() => setQuality(q)}
                              className={`ann-create-quality-selector ${isSelected ? "ann-create-quality-selector-selected" : ""} ann-quality-${q.toLowerCase()}`}
                           >
                              <div className={`ann-create-quality-indicator-circle ${isSelected ? "ann-create-quality-indicator-circle-selected" : ""}`} />
                              <span className={`ann-create-quality-label ${isSelected ? "ann-create-quality-label-selected" : ""}`}>{label}</span>
                           </div>
                        );
                     })}
                  </div>
               </div>
               {canVerify && (
                  <div>
                     <label
                        className={`ann-create-verifyCheckboxLabel ${verifyStamp ? "ann-create-verifyCheckboxLabel--checked" : ""}`}
                     >
                        <input
                           type="checkbox"
                           checked={verifyStamp}
                           onChange={(e) => setVerifyStamp(e.target.checked)}
                           className="ann-create-verifyCheckbox"

                        />
                        <span
                           style={{ fontSize: "20px" }}
                           className="ann-create-verifyIcon"
                        >
                           <MIBIcon icon="userVerified" />
                        </span>
                        <span className="ann-create-verifyText">
                           {Localization.getText("Stamp this annotation as verified")}
                        </span>
                     </label>
                  </div>
               )}
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Title")}
                  </label>
                  <input
                     value={title}
                     onChange={e => setTitle(e.target.value)}
                     className="ann-create-input"
                  />
               </div>
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Note text")}
                  </label>
                  <textarea
                     rows={6}
                     value={text}
                     onChange={e => setText(e.target.value)}
                     className="ann-create-textarea"
                  />
               </div>
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("History comment")}
                  </label>
                  <textarea
                     rows={3}
                     value={historyComment}
                     onChange={e => setHistoryComment(e.target.value)}
                     className="ann-create-textarea"
                     placeholder={Localization.getText("Optional history comment")}
                  />
               </div>
               {error && (
                  <div className="ann-create-error">{error}</div>
               )}
               <div className="ann-create-button-group">
                  <GlassButton disabled={saving} onClick={handleCreate}>
                     {saving ? Localization.getText("Saving...") : (editAnnotation ? Localization.getText("Save") : Localization.getText("Create"))}
                  </GlassButton>
                  <GlassButton onClick={onClose}>{Localization.getText("Cancel")}</GlassButton>
               </div>
            </div>
         </ADModalBody>
      </AdvancedDragModal>,
      document.body
   );
}
